import React, { useState, useEffect, useCallback } from 'react';
import config from "../../common/Config";
import { toast } from "sonner";
import AddCustomersForm from './AddCustomersForm';
import { pushQuickBooksMaster, resolveQuickBooksEnabled } from "../../../utils/quickBooksTenant";
import {
  Box,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Menu,
  MenuItem,
  TablePagination,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  InputAdornment,
  Skeleton,
  ListItemIcon,
  ListItemText,
  Grid2,
  CircularProgress,
} from '@mui/material';
import LastPageDoubleArrowIcon from '../../common/LastPageDoubleArrowIcon';
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import '../../common/commonStyles.css';
import { encryptPayload, resolveEncryptedResponse } from '../../../utils/customerCrypto';
import { getCompanySettings, resolveCustomerApiEncrypt } from '../../../utils/companySettingsCache';
import { useRole } from '../../../contexts/RoleContext';
import { createAdminCustomerImpersonationRedirect } from '../../../utils/adminCustomerImpersonationApi';

const Customers = () => {
  const accessToken = localStorage.getItem('accessToken');
  const { hasPermission } = useRole();
  const canViewCustomerAsAdmin = hasPermission('ViewCustomer') || hasPermission('Customers');
  const [showQuickBooks, setShowQuickBooks] = useState(false);
  const [pushingQb, setPushingQb] = useState(false);
  const [viewCustomerLoading, setViewCustomerLoading] = useState(false);
  const company_tax_type = localStorage.getItem('company_tax_type');
  const showContraPrice = localStorage.getItem("showContraPrice");
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdCustomerId, setPwdCustomerId] = useState(null);
  const [pwdCustomerName, setPwdCustomerName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [page, setPage] = useState(0);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [addDynamicCustomerDialogOpen, setAddDynamicCustomerDialogOpen] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");
 

  const getApiUrl = () => {
    if (tabValue === 0) {
      return `${config.API_URL}/get_active_customer`;
    } else if (tabValue === 1) {
      return `${config.API_URL}/get_inctive_customer`;
    } else {
      return `${config.API_URL}/get_new_customer`;
    }
  };

  const fetchData = useCallback(async () => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      formData.append("draw", page + 1);
      formData.append("start", page * rowsPerPage);
      formData.append("length", rowsPerPage);
      formData.append("search[value]", searchValue || "");
      formData.append("search[regex]", "false");
      formData.append("searchValue", searchValue || "");

      const requestData = {
        draw: page + 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        'search[value]': searchValue || "",
        'search[regex]': "false",
        searchValue: searchValue || "",
      };
      if (useEncrypt) {
        const encryptedPayload = encryptPayload(requestData);
        if (encryptedPayload) {
          formData.append("encrypted_payload", encryptedPayload);
        }
      }
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token || accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
          console.error("API Error Response:", errorText);
        } catch (e) {
          console.error("Could not read error response");
        }
        throw new Error(`HTTP error! status: ${response.status}. ${errorText}`);
      }

      const json = await response.json();
      let resolved;
      if (useEncrypt) {
        const decryptedResponse = resolveEncryptedResponse(json);
        resolved = decryptedResponse && typeof decryptedResponse === 'object' ? decryptedResponse : json;
      } else {
        resolved = json;
      }

      if (resolved?.data && Array.isArray(resolved.data)) {
        setCustomers(resolved.data);
        setTotalRecords(resolved.recordsTotal || resolved.recordsFiltered || resolved.data.length);
      } else if (resolved && Array.isArray(resolved)) {
        setCustomers(resolved);
        setTotalRecords(resolved.length);
      } else {
        setCustomers([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch customers");
      setCustomers([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchValue, tabValue, accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    resolveQuickBooksEnabled().then(setShowQuickBooks);
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchValue('');
    setSearchInputValue('');
    setPage(0);
  };

  const handlePushQuickBooks = async () => {
    if (pushingQb) return;
    setPushingQb(true);
    try {
      const data = await pushQuickBooksMaster("customers");
      toast.success(data.message || data.msg || "Customers pushed to QuickBooks");
    } catch (e) {
      toast.error(e.message || "Push failed");
    } finally {
      setPushingQb(false);
    }
  };

  const handleMenuOpen = (event, rowData) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedRowData(rowData);
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchor(null);
    setSelectedRowData(null);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setSearchValue(value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleChangeStatus = async (id, status) => {
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const formData = new FormData();
    formData.append('status', status);
    formData.append('id', id);
    if (useEncrypt) {
      const encryptedPayload = encryptPayload({ id, status });
      if (encryptedPayload) {
        formData.append('encrypted_payload', encryptedPayload);
      }
    }
    const apiUrl = `${config.API_URL}/update_customer_status`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to change status');
      }

      const data = await response.json();
      toast.success('Status changed successfully.');
      fetchData();
    } catch (error) {
      console.error('Error status change:', error);
      toast.error("Failed to change status");
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setPwdLoading(true);
    try {
      const formData = new FormData();
      formData.append('customer_id', pwdCustomerId);
      formData.append('password', newPassword);
      const response = await fetch(
        `${config.API_URL}/update_customer_password`,
        {
          method: "POST",
          headers: {
            authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const data = await response.json();
      if (data.status === true) {
        toast.success("Password updated successfully.");
        setShowPwdModal(false);
        setNewPassword('');
        setPwdCustomerId(null);
        setPwdCustomerName('');
      } else {
        toast.error(data.message || "Password update failed.");
      }
    } catch (error) {
      toast.error("Password update failed.");
    }
    setPwdLoading(false);
  };

  const handleViewCustomer = async (customer) => {
    if (!canViewCustomerAsAdmin) {
      toast.error("You do not have permission to view customer login.");
      return;
    }
    const customerId = customer?.id;
    if (!customerId) {
      toast.error("Customer not found.");
      return;
    }
    if (viewCustomerLoading) return;
    setViewCustomerLoading(true);
    try {
      const { redirectUrl } = await createAdminCustomerImpersonationRedirect(
        customerId
      );
      window.open(redirectUrl, "_blank", "noopener,noreferrer");
      toast.success("Opening customer session…");
    } catch (error) {
      toast.error(
        error?.message || "Unable to open customer view. Try again."
      );
    } finally {
      setViewCustomerLoading(false);
    }
  };

  const renderTable = () => {
    const isAtrPrefix = accessToken === 'atr' || config.PREFIX === 'atr';
    const isNewCustomersTab = tabValue === 2;
    let headers;
    if (isNewCustomersTab) {
      headers = ["#", "A/C No.", "Company", "Name", "Email Address", "Phone", "Created By", "Created At", "Action"];
    } else if (isAtrPrefix) {
      headers = ["#", "A/C No.", "Company", "Name", "Email Address", "Phone", "Address", "Postcode", "Action"];
    } else {
      headers = ["#", "A/C No.", "Company", "Name", "Email Address", "Phone", "Postcode", "Product Band", "Action"];
    }

    return (
      <TableContainer>
        <Table
          size="medium"
          className="table-cell-border"
          sx={{ tableLayout: "fixed" }}
        >
          <TableHead>
            <TableRow className="table-cell-header">
              {headers.map((header, idx) => {
                let align = "left";
                let width = 'auto';
                if (header === "#") {
                  align = "center";
                  width = '50px';
                } else if (header === "A/C No.") {
                  width = '70px';
                } else if (header === "Company") {
                  width = '170px';
                } else if (header === "Name") {
                  width = '180px';
                } else if (header === "Email Address") {
                  width = '150px';
                } else if (header === "Phone") {
                  width = '100px';
                } else if (header === "Address") {
                  width = '200px';
                } else if (header === "Postcode") {
                  width = '100px';
                } else if (header === "Created By") {
                  width = '120px';
                } else if (header === "Created At") {
                  width = '140px';
                } else if (header.includes("No.")) {
                  width = '100px';
                } else if (header === "Product Band") {
                  width = '90px';
                } else if (header === "Action") {
                  align = "center";
                  width = '80px';
                }
                return (
                  <TableCell
                    key={`header-${idx}-${header}`}
                    align={align}
                    sx={{ width: width }}
                  >
                    {header}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={headers.length}>
                    <Skeleton height={40} />
                  </TableCell>
                </TableRow>
              ))
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length} align="center" sx={{ py: 4 }}>
                  <Typography className='commonColor'>
                    No records found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              customers.map((row, index) => (
                <TableRow
                  key={`row-${row.id || row.customer_id || index}-${page}`}
                  hover
                  className="table-cell-style"
                  sx={{
                    "&:hover": {
                      backgroundColor: "rgba(145, 158, 171, 0.04)"
                    },
                  }}
                >
                  <TableCell align="center" width="50px">
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell align="left" width="80px">
                    {row.account_no || ''}
                  </TableCell>
                  <TableCell align="left" width="120px">
                    {row.company || ''}
                  </TableCell>
                  <TableCell align="left" width="180px">
                    {row.name || ''}
                  </TableCell>
                  <TableCell align="left" width="200px">
                    {row.email?.toLowerCase() || ''}
                  </TableCell>
                  <TableCell align="left" width="120px">
                    {row.phone || ''}
                  </TableCell>
                  {isNewCustomersTab ? (
                    <>
                      <TableCell align="left" width="120px">
                        {row.created_by || row.createdBy || row.create_by || ''}
                      </TableCell>
                      <TableCell align="left" width="140px">
                        {row.created_at || row.createdAt || row.create_at || row.created_date || ''}
                      </TableCell>
                    </>
                  ) : isAtrPrefix ? (
                    <>
                      <TableCell align="left" width="200px">
                        {row.address || ''}
                      </TableCell>
                      <TableCell align="left" width="100px">
                        {row.postal_code || ''}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="left" width="100px">
                        {row.postal_code || ''}
                      </TableCell>
                      <TableCell align="center" width="110px">
                        {row.product_price_type || ''}
                      </TableCell>
                    </>
                  )}
                  <TableCell align="center" width="80px">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, { ...row, id: row.id || row.customer_id });
                      }}
                      sx={{
                        color: "#637381",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)"
                        }
                      }}
                    >
                      <Iconify icon="eva:more-vertical-fill" width={18} height={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box>
      <AddCustomersForm open={addCustomerDialogOpen} onClose={() => setAddCustomerDialogOpen(false)} onSuccess={fetchData} />
      <AddCustomersForm
        open={addDynamicCustomerDialogOpen}
        onClose={() => setAddDynamicCustomerDialogOpen(false)}
        onSuccess={fetchData}
        useDynamicForm
      />
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <Grid2 size={{ xs: 12, md: 2 }}>
              <Typography className="page-title">
                Customers
              </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4}}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  className="common-tabs"
                >
                  <Tab label="Customers" />
                  <Tab label="Inactive Customers" />
                  <Tab label="New Customers" />
                </Tabs>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }} gap={1} display="flex" flexDirection={{ xs: "column", md: "row"}} alignItems={{ md: "center" }} justifyContent="flex-end" flexWrap="nowrap" mt={{ xs: 2, md: 0 }}>
                <TextField
                  className="input-border"
                  size="small"
                  placeholder="Search customers..."
                  value={searchInputValue}
                  onChange={handleSearchChange}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      )
                    }
                  }}
                  sx={{
                    width: { xs: '100%', md: 220 },
                    minWidth: { md: 180 },
                    flexShrink: 1,
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="mingcute:add-line" width={18} />}
                  onClick={() => setAddCustomerDialogOpen(true)}
                  className="button-variant-contained"
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Add Customer
                </Button>
                {(config.PREFIX === "ebu" || accessToken === "ebu" || config.PREFIX === "rhp" || accessToken === "rhp") && (
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="mingcute:add-line" width={18} />}
                  onClick={() => setAddDynamicCustomerDialogOpen(true)}
                  className="button-variant-outlined"
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Add Customer D
                </Button>
                )}
                {showQuickBooks && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    disabled={pushingQb}
                    onClick={handlePushQuickBooks}
                    sx={{
                      color: "#000",
                      borderColor: "#000",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      height: 34,
                      "&:hover": {
                        borderColor: "#000",
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    {pushingQb ? "Pushing..." : "Push to QuickBooks"}
                  </Button>
                )}
              </Grid2>
            </Grid2>

          </Box>
          {renderTable()}
          {customers.length > 0 && (
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <TablePagination
                component="div"
                count={totalRecords}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                showLastButton
                slots={{ actions: { lastButtonIcon: LastPageDoubleArrowIcon } }}
                className="pagination-styles"
              />
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog open={showPwdModal} onClose={() => !pwdLoading && setShowPwdModal(false)} maxWidth="sm" fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Change Password
            </Typography>
            {pwdCustomerName && (
              <Typography component="span" className='commonColor' sx={{ fontWeight: 400, fontSize: 14,}}>
                ({pwdCustomerName})
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => !pwdLoading && setShowPwdModal(false)} sx={{ p: 0.5 }}>
            <Iconify icon="eva:close-fill" width={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 0 }}>
          <TextField
          className="form-input-border"
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="Enter new password"
            disabled={pwdLoading}
            autoComplete="new-password"
            inputProps={{ minLength: 6 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row'}} gap={1}>
          <Button onClick={() => setShowPwdModal(false)} 
          disabled={pwdLoading}
          variant="outlined"
           className="button-variant-outlined" sx={{ textTransform: 'none', width: { xs: '100%', md: 'auto' } }}>
            Cancel
          </Button>
          <Button onClick={handlePasswordChange}
            disabled={pwdLoading}
            className="button-variant-contained"
            variant="contained" sx={{ width: { xs: '100%', md: 'auto' } }}>
            {pwdLoading ? "Saving..." : "Change Password"}
          </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          onClick: (e) => e.stopPropagation(),
          sx: {
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mt: 1,
          },
        }}
      >
        {selectedRowData && (
          <>
            <MenuItem
              onClick={() => {
                if (selectedRowData) {
                  const newStatus = selectedRowData.status == "1" ? 0 : 1;
                  handleChangeStatus(selectedRowData.id, newStatus);
                }
                handleMenuClose();
              }}
              sx={{
                color: selectedRowData.status == "1" ? "#FF5630" : "#22C55E",
                "& .MuiListItemIcon-root": {
                  color: selectedRowData.status == "1" ? "#FF5630" : "#22C55E",
                },
                "& .MuiTypography-root": {
                  color: selectedRowData.status == "1" ? "#FF5630 !important" : "#22C55E !important",
                  fontWeight: 600,
                },
              }}
            >
              <ListItemIcon>
                <Iconify icon="la:toggle-on" width={20} height={20} />
              </ListItemIcon>
              <ListItemText primary={selectedRowData.status == "1" ? "Deactivate" : "Activate"} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (selectedRowData) {
                  window.location.href = `${config.BASE_URL}/customer/edit_customer/${selectedRowData.id}`;
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <Iconify icon="solar:pen-bold" width={18} />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            {(config.PREFIX === "ebu" || accessToken === "ebu" || config.PREFIX === "rhp" || accessToken === "rhp") && (
            <MenuItem
              onClick={() => {
                if (selectedRowData) {
                  window.location.href = `${config.BASE_URL}/customer/edit_customer_dynamic/${selectedRowData.id}`;
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <Iconify icon="solar:pen-bold" width={18} />
              </ListItemIcon>
              <ListItemText>Edit D</ListItemText>
            </MenuItem>
            )}
            {canViewCustomerAsAdmin && (accessToken == 'atr') && (
              <MenuItem
                disabled={viewCustomerLoading}
                onClick={() => {
                  const row = selectedRowData;
                  handleMenuClose();
                  if (row) {
                    handleViewCustomer(row);
                  }
                }}
              >
                <ListItemIcon>
                  {viewCustomerLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Iconify icon="solar:eye-bold" width={18} />
                  )}
                </ListItemIcon>
                <ListItemText>
                  {viewCustomerLoading ? "Opening…" : "Login as Customer"}
                </ListItemText>
              </MenuItem>
            )}
            {showContraPrice === 'Y' && (
              <MenuItem
                onClick={() => {
                  if (selectedRowData) {
                    window.location.href = `${config.BASE_URL}/customer/contra_price/${selectedRowData.id}`;
                  }
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <Iconify icon="solar:tag-bold" width={18} />
                </ListItemIcon>
                <ListItemText>Contra Price</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                if (selectedRowData) {
                  setPwdCustomerId(selectedRowData.id);
                  setPwdCustomerName(selectedRowData.name || '');
                  setShowPwdModal(true);
                  setNewPassword('');
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <Iconify icon="solar:key-bold" width={18} />
              </ListItemIcon>
              <ListItemText>Change Password</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default Customers;
